/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { css, jsx } from '@emotion/core'
import { Button } from '@material-ui/core'
import organizationService from '../../services/organization'
import { theme } from '../../constants'

const styles = {}
styles.container = css`
  margin: auto;
  padding: 0 24px 24px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
`

styles.title = css`
  color: white;
  text-align: center;
  margin-bottom: 50px;
  font-size: 30px;
  font-weight: 600;
  line-height: 30px;
  padding-right: 5px;
  padding-left: 5px;
  width: 100%;
`

styles.subtitle = css`
  color: ${theme.palette.text.primary};
  text-align: center;
  margin-bottom: 20px;
`

styles.description = css`
  color: ${theme.palette.text.primary};
`

styles.buttonsContainer = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 30px;
`

styles.buttonContainer = css`
  display: flex;
  width: 100%;
  justify-content: center;
`

styles.productLogo = css`
  display: block;
  margin: 0 auto 24px auto;
  max-width: 260px;
  width: 60%;
  height: auto;
`

styles.acceptButton = css`
  text-transform: none;
  border-radius: 8px;
  width: 100%;
  max-width: 320px;
  padding: 8px 16px;
  white-space: nowrap;
  background: var(--metrica-green);
  color: ${theme.palette.text.primary};
  border: 1px solid transparent;

  &:hover {
    filter: brightness(1.1);
  }
`

styles.declineButton = css`
  text-transform: none;
  border-radius: 8px;
  width: 100%;
  max-width: 320px;
  padding: 8px 16px;
  white-space: nowrap;
  background: transparent;
  color: ${theme.palette.error.main};
  border: 1px solid transparent;

  &:hover {
    background: transparent;
    border: 1px solid ${theme.palette.error.main};
  }
`

styles.htmlLink = `
  color: var(--metrica-green);
  text-decoration: underline;
`

function AcceptInvitation(props) {
  const isProduction = process.env.REACT_APP_ENV === 'production'
  const signUpUrl = isProduction
    ? 'https://downloads.metrica-sports.com'
    : 'http://downloads-dev.metrica-sports.com'

  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const onCancel = () => {
    window.open(`${signUpUrl}/decline-invitation`, '_self')
  }

  const onAccept = async () => {
    try {
      setIsSubmitting(true)
      const response = await organizationService.acceptInvitation({ token: props.token })
      if (response.data.redirectUrl) {
        window.location.href = response.data.redirectUrl
      } else {
        window.open(`${signUpUrl}/accept-invitation`, '_self')
      }
    } catch (e) {
      toast.error(window.I18n.t('error.tryAgain'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div css={styles.container}>
      <img css={styles.productLogo} src={props.product.logo} alt={props.product.displayName} />
      <div css={styles.title}>{window.I18n.t('invitation.title')}</div>

      <div css={styles.subtitle}>{window.I18n.t('invitation.subtitle')}</div>

      <div css={styles.description}>{window.I18n.t('invitation.description1')}</div>
      <br />

      <div css={styles.description}>{window.I18n.t('invitation.description2')}</div>
      <ol css={styles.description}>
        <li>{window.I18n.t('invitation.list1')}</li>
        <li>{window.I18n.t('invitation.list2')}</li>
        <li>{window.I18n.t('invitation.list3')}</li>
        <li>{window.I18n.t('invitation.list4')}</li>
        <li>{window.I18n.t('invitation.list5')}</li>
        <li>{window.I18n.t('invitation.list6')}</li>
        <li>{window.I18n.t('invitation.list7')}</li>
        <li
          dangerouslySetInnerHTML={{
            __html: window.I18n.t('invitation.list8', {
              email: props.from,
              pre1: `<a style="${styles.htmlLink}" href=mailto:${props.from} target="_blank" rel="noopener noreferrer">`,
              post1: '</a>',
              pre2: `<a style="${styles.htmlLink}" href="https://www.metrica-sports.com/terms-conditions" target="_blank" rel="noopener noreferrer">`,
              post2: '</a>',
              pre3: `<a style="${styles.htmlLink}" href="https://www.metrica-sports.com/privacy-policy" target="_blank" rel="noopener noreferrer">`,
              post3: '</a>'
            })
          }}
        />
      </ol>

      <div css={styles.buttonsContainer}>
        <div css={styles.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            css={styles.declineButton}
            style={isSubmitting ? { opacity: 0.5, pointerEvents: 'none' } : {}}
            onClick={onCancel}
          >
            {window.I18n.t('invitation.cancelButton')}
          </Button>
        </div>

        <div css={styles.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            css={styles.acceptButton}
            style={{
              background: props.product.buttonBackground,
              color: props.product.buttonColor,
              ...(isSubmitting ? { opacity: 0.5, pointerEvents: 'none' } : {})
            }}
            onClick={onAccept}
          >
            {window.I18n.t(isSubmitting ? 'invitation.accepting' : 'invitation.acceptButton')}
          </Button>
        </div>
      </div>
    </div>
  )
}

AcceptInvitation.propTypes = {
  product: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  from: PropTypes.string.isRequired
}

export default AcceptInvitation
